'use client'

import { ChevronLeft, ChevronRight } from 'lucide-react'
import type { ComponentProps } from 'react'
import { useEffect, useRef } from 'react'
import { type DayButton, DayPicker, getDefaultClassNames } from 'react-day-picker'
import { pl } from 'react-day-picker/locale'
import { cn } from '../lib/utils.ts'

/**
 * The shapes a caller needs to hold a selection, re-exported so the calendar's
 * date library stays an implementation detail of this package rather than a
 * dependency every consuming app has to list and keep in step.
 */
export type { DateRange, Matcher } from 'react-day-picker'

/**
 * Date picker built on `react-day-picker`, styled to the token layer — the
 * shadcn Calendar, trimmed to what this product uses.
 *
 * Dropped from upstream: the dropdown caption layout (month/year `<select>`s),
 * week numbers and the RTL chevron flip. None of them are reachable here — the
 * only caller picks a rental window a few weeks out, in Polish — and each one
 * carried a slot override plus its own classNames branch.
 *
 * Everything else is upstream's, including the `--cell-size` variable the grid
 * is measured in: change it on the root (`[--cell-size:2.25rem]`) and the whole
 * calendar scales, rather than tuning six paddings.
 *
 * The locale defaults to Polish — the product's canonical one, and the same
 * reason `DialogClose` says "Zamknij" — never to the browser's: the week has to
 * start on Monday whatever the visitor's OS is set to. Pass `locale` to change
 * it once there is a second language.
 *
 * `classNames` REPLACES a slot rather than adding to it, as upstream does, so
 * an override states the whole slot.
 */
export type CalendarProps = ComponentProps<typeof DayPicker>

export function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  locale = pl,
  ...props
}: CalendarProps) {
  const defaults = getDefaultClassNames()

  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      locale={locale}
      className={cn('group/calendar p-3 [--cell-size:2.25rem]', defaults.root, className)}
      classNames={{
        root: cn('w-fit', defaults.root),
        months: cn('relative flex flex-col gap-4 md:flex-row', defaults.months),
        month: cn('flex w-full flex-col gap-4', defaults.month),
        nav: cn('absolute inset-x-0 top-0 flex items-center justify-between gap-1', defaults.nav),
        button_previous: cn(NAV_BUTTON, defaults.button_previous),
        button_next: cn(NAV_BUTTON, defaults.button_next),
        month_caption: cn(
          'flex h-(--cell-size) items-center justify-center px-(--cell-size)',
          defaults.month_caption,
        ),
        caption_label: cn('text-sm font-medium capitalize select-none', defaults.caption_label),
        month_grid: cn('w-full border-collapse', defaults.month_grid),
        weekdays: cn('flex', defaults.weekdays),
        weekday: cn(
          'flex-1 text-[0.75rem] font-normal text-muted-foreground capitalize select-none',
          defaults.weekday,
        ),
        week: cn('mt-1 flex w-full', defaults.week),
        day: cn(
          'group/day relative aspect-square h-full w-full p-0 text-center select-none',
          defaults.day,
        ),
        // The three range slots paint the CELL, so the highlight runs edge to
        // edge between days; the button inside paints only the two ends.
        range_start: cn('rounded-l-md bg-accent', defaults.range_start),
        range_middle: cn('rounded-none bg-accent', defaults.range_middle),
        range_end: cn('rounded-r-md bg-accent', defaults.range_end),
        today: cn('rounded-md ring-1 ring-ring/60 ring-inset', defaults.today),
        outside: cn('text-muted-foreground/60', defaults.outside),
        disabled: cn('text-muted-foreground opacity-40', defaults.disabled),
        hidden: cn('invisible', defaults.hidden),
        ...classNames,
      }}
      components={{
        Root: ({ className: rootClassName, rootRef, ...rootProps }) => (
          <div data-slot="calendar" ref={rootRef} className={rootClassName} {...rootProps} />
        ),
        Chevron: ({ className: chevronClassName, orientation, ...chevronProps }) =>
          orientation === 'left' ? (
            <ChevronLeft className={cn('size-4', chevronClassName)} {...chevronProps} />
          ) : (
            <ChevronRight className={cn('size-4', chevronClassName)} {...chevronProps} />
          ),
        DayButton: CalendarDayButton,
      }}
      {...props}
    />
  )
}

const NAV_BUTTON =
  'inline-flex size-(--cell-size) items-center justify-center rounded-md text-muted-foreground transition-colors select-none hover:bg-accent hover:text-accent-foreground focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none aria-disabled:pointer-events-none aria-disabled:opacity-40'

/**
 * A day cell's button. Split out because it needs an effect: react-day-picker
 * tracks the focused day itself and only reports it as a modifier, so the DOM
 * focus has to follow — without this, arrow keys move the highlight while the
 * browser's focus stays on the day the user first tabbed to.
 */
export function CalendarDayButton({
  className,
  day,
  modifiers,
  ...props
}: ComponentProps<typeof DayButton>) {
  const ref = useRef<HTMLButtonElement>(null)
  // `Modifiers` carries an index signature, so every flag is read by key.
  const focused = modifiers['focused']
  const start = modifiers['range_start']
  const middle = modifiers['range_middle']
  const end = modifiers['range_end']

  useEffect(() => {
    if (focused) ref.current?.focus()
  }, [focused])

  const single = modifiers['selected'] && !start && !middle && !end

  return (
    <button
      ref={ref}
      type="button"
      data-day={day.date.toLocaleDateString()}
      data-selected-single={single || undefined}
      data-range-start={start || undefined}
      data-range-end={end || undefined}
      data-range-middle={middle || undefined}
      className={cn(
        'flex aspect-square size-auto w-full min-w-(--cell-size) items-center justify-center rounded-md text-sm leading-none font-normal transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none',
        'data-[selected-single=true]:bg-primary data-[selected-single=true]:text-primary-foreground data-[selected-single=true]:hover:bg-primary',
        'data-[range-start=true]:bg-primary data-[range-start=true]:text-primary-foreground data-[range-start=true]:hover:bg-primary',
        'data-[range-end=true]:bg-primary data-[range-end=true]:text-primary-foreground data-[range-end=true]:hover:bg-primary',
        'data-[range-middle=true]:rounded-none data-[range-middle=true]:bg-transparent data-[range-middle=true]:text-accent-foreground',
        className,
      )}
      {...props}
    />
  )
}
